import { createSignal } from "solid-js";

/**
 * Konkord: animations rest while nobody is looking. After a few minutes
 * without mouse, keyboard, scroll or touch, or a moment after the app is
 * hidden (minimized, another tab), looping videos (GIF memes) pause,
 * animated pictures show a still frame and marked GIFs freeze. Any input or
 * showing the app again brings everything back at once.
 *
 * Calls are not touched here: a stream or camera the user watches keeps
 * playing while they sit still (hidden video pauses in the voice state).
 */

/** no input for this long → rest */
const IDLE_MS = 3 * 60_000;
/** hidden for this long → rest (a quick alt-tab doesn't count) */
const HIDDEN_MS = 2000;
/** how often the idle time is checked */
const CHECK_MS = 10_000;

/** attribute marking an animated <img> that may be frozen */
export const FREEZE_ATTRIBUTE = "data-konkord-gif";

const [asleep, setAsleep] = createSignal(false);

/**
 * Konkord: whether animations are resting right now
 */
export { asleep };

let lastInput = Date.now();
let hiddenTimer: ReturnType<typeof setTimeout> | undefined;
const pausedVideos = new Set<HTMLVideoElement>();
const stills = new Map<HTMLImageElement, HTMLCanvasElement>();

function rest() {
  if (asleep()) return;
  setAsleep(true);

  for (const video of document.querySelectorAll<HTMLVideoElement>(
    "video[loop]",
  )) {
    if (!video.paused) {
      video.pause();
      pausedVideos.add(video);
    }
  }

  for (const img of document.querySelectorAll<HTMLImageElement>(
    `img[${FREEZE_ATTRIBUTE}]`,
  )) {
    if (!img.complete || !img.naturalWidth || !img.isConnected) continue;
    // the frame showing now, drawn once, in place of the moving picture
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext("2d")?.drawImage(img, 0, 0);
    canvas.className = img.className;
    canvas.style.width = `${img.offsetWidth}px`;
    canvas.style.height = `${img.offsetHeight}px`;
    img.before(canvas);
    img.style.display = "none";
    stills.set(img, canvas);
  }
}

function wake() {
  if (!asleep()) return;
  setAsleep(false);

  for (const video of pausedVideos)
    if (video.isConnected) void video.play().catch(() => {});
  pausedVideos.clear();

  for (const [img, canvas] of stills) {
    canvas.remove();
    img.style.display = "";
  }
  stills.clear();
}

function onInput() {
  lastInput = Date.now();
  if (!document.hidden) wake();
}

function onVisibility() {
  clearTimeout(hiddenTimer);
  if (document.hidden) hiddenTimer = setTimeout(rest, HIDDEN_MS);
  else onInput();
}

if (typeof document !== "undefined") {
  for (const event of [
    "pointermove",
    "pointerdown",
    "keydown",
    "wheel",
    "touchstart",
  ])
    window.addEventListener(event, onInput, { passive: true, capture: true });
  document.addEventListener("visibilitychange", onVisibility);
  setInterval(() => {
    if (!asleep() && Date.now() - lastInput > IDLE_MS) rest();
  }, CHECK_MS);
}

/**
 * Konkord: an animated picture's address, or its still preview while
 * animations rest (Stoat serves the moving GIF at ".../original" and a still
 * preview of avatars and icons without it)
 * @param url Picture address
 * @returns Address to show now
 */
export function stillWhenAsleep(url: string | undefined) {
  return url && asleep() && /\/(avatars|icons)\/[^/]+\/original$/.test(url)
    ? url.slice(0, -"/original".length)
    : url;
}
